import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SmartImage from "./SmartImage";
import { Headline, Paragraph, Section, ContentBlock, Button } from "./ui";

// Lazy loading video component for HighlightCardGrid
const LazyGridVideo = ({ videoSrc, poster, className }) => {
  const videoRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !shouldLoad) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1, // Load when 10% of the video is visible
        rootMargin: "50px", // Start loading 50px before it comes into view
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (shouldLoad && videoRef.current && videoSrc) {
      videoRef.current.src = `${videoSrc}`;
      videoRef.current.load();
    }
  }, [shouldLoad, videoSrc]);

  return (
    <video
      ref={videoRef}
      autoPlay={shouldLoad}
      loop
      muted
      playsInline
      preload="none"
      poster={poster}
      className={`${className} ${
        isLoaded ? "opacity-100" : "opacity-90"
      } transition-opacity duration-500`}
      onLoadedData={() => setIsLoaded(true)}
      type="video/mp4"
    />
  );
};

const HighlightCardGrid = ({ content, highQuality = false }) => {
  // Early return if no content provided
  if (!content || content.length === 0) {
    return null;
  }

  return (
    <Section
      variant="white"
      spacing="tight"
      margin={false}
      className="py-2 sm:py-3"
    >
      <ContentBlock maxWidth="none" spacing="tight">
        <div className="">
          <div className="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
              {content.map((link, index) => (
                <Link
                  key={index}
                  to={link.url}
                  className={`relative ${
                    link.isLight
                      ? "bg-[var(--color-secondary)]"
                      : "bg-[var(--color-black)]"
                  } ${
                    link.isVideo ? "overflow-hidden" : ""
                  } p-4 sm:py-8 xl:py-12 pt-8 md:pt-12 xl:pt-16 rounded-sm transition-all duration-300 flex flex-col hover:shadow-xl cursor-pointer group`}
                >
                  {/* Video Background for video items */}
                  {link.isVideo && (
                    <>
                      <LazyGridVideo
                        videoSrc={link.videoSrc}
                        poster={`${link.posterSrc}`}
                        className="absolute inset-0 w-full h-full object-cover z-0 transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/60 z-10"></div>
                    </>
                  )}

                  {/* Content wrapper with z-index for video items */}
                  <div
                    className={`relative ${
                      link.isVideo ? "z-20" : ""
                    } flex flex-col h-full mx-auto`}
                  >
                    {/* Centered Atmo Logo for video items */}
                    {/* {link.isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-48 h-48 opacity-90">
                          <SmartImage
                            src="atmo-logo.png"
                            alt="Atmo Logo"
                            className="w-full h-full object-contain drop-shadow-2xl"
                            width={128}
                            height={128}
                          />
                        </div>
                      </div>
                    )} */}

                    <Headline
                      noSpacing
                      variant="h4"
                      className={`${
                        link.isLight
                          ? "text-[var(--color-primary)]"
                          : "text-white"
                      }  text-center `}
                    >
                      {link.headline}
                    </Headline>
                    <Paragraph
                      variant="medium"
                      className={`text-center ${
                        link.isLight ? "text-gray-600" : "text-gray-300"
                      } my-6 max-w-xl`}
                    >
                      {link.paragraph}
                    </Paragraph>

                    {/* Engine Image - only for non-video items */}
                    {!link.isVideo && (
                      <div className="w-full h-40 sm:h-60 overflow-hidden mb-4 max-w-sm mx-auto">
                        <SmartImage
                          src={link.image}
                          alt={`${link.headline} Engine`}
                          className="max-h-40 sm:max-h-none sm:h-full object-cover transition-transform duration-300 group-hover:scale-105 mx-auto"
                          width={200}
                          height={150}
                          crop="fill"
                          gravity="center"
                          highQuality={highQuality}
                        />
                      </div>
                    )}

                    {/* Häkchenliste für alle Stufen */}
                    {/* {link.checklistItems && (
                  <ul className="text-sm text-gray-200 space-y-2 mb-6">
                    {link.checklistItems.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-green-400 mt-1"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )} */}

                    {/* Spacer to push content to center */}
                    <div className="flex-grow"></div>

                    <Button
                      as={Link}
                      to={link.url}
                      variant={link.isLight ? "primary" : "outline"}
                      size="medium"
                      className="w-fit mx-auto my-6"
                    >
                      Mehr erfahren →
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </ContentBlock>
    </Section>
  );
};

export default HighlightCardGrid;
